// TikTok-канал (TikTok Direct, Business Messaging API) — сервис API.
// Базовый префикс: /api/v1/integrations/providers/tiktok (handoff §3).
// Ошибки TikTok-роутов: detail — СТРОКА (кроме pydantic-валидации, где массив).
// Бросаем TikTokApiError(status + detail) — парсится через utils/tiktokErrorParser.

import authService from './auth.service';
import { buildQuery } from '../utils/buildQuery';
import type {
  TikTokInstanceOut,
  TikTokInstanceCreatePayload,
  TikTokDeleteInstanceResult,
  TikTokOAuthStartOut,
  TikTokChatOut,
  TikTokMessageOut,
  TikTokSendTextPayload,
} from '../types/tiktok.types';

/** Ошибка TikTok-API: несёт HTTP-статус и verbatim detail для парсера/UI. */
export class TikTokApiError extends Error {
  readonly status?: number;
  readonly detail: string;

  constructor(detail: string, status?: number) {
    super(detail);
    this.name = 'TikTokApiError';
    this.detail = detail;
    this.status = status;
  }
}

/** Достаёт detail из тела ответа ({ detail: string } или Pydantic-массив). */
async function extractDetail(res: Response): Promise<string> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // body — не JSON
  }
  const detail = (body as { detail?: unknown } | null | undefined)?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d as { msg?: unknown })?.msg)
      .filter((m): m is string => typeof m === 'string');
    if (msgs.length) return msgs.join('; ');
  }
  return res.statusText || 'Неизвестная ошибка';
}

class TikTokService {
  private readonly BASE = '/api/v1/integrations/providers/tiktok';

  // ─────────────── Инстансы (каналы) ───────────────

  async listInstances(opts?: { is_active?: boolean }): Promise<TikTokInstanceOut[]> {
    const is_active = opts?.is_active ?? true;
    return this.request<TikTokInstanceOut[]>(
      `${this.BASE}/instances${buildQuery({ is_active })}`,
    );
  }

  async createInstance(body: TikTokInstanceCreatePayload): Promise<TikTokInstanceOut> {
    return this.request<TikTokInstanceOut>(`${this.BASE}/instances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // НЕОБРАТИМО (каскад: чаты, сообщения, credentials). Caller обязан показать confirm.
  async deleteInstance(instanceId: number): Promise<TikTokDeleteInstanceResult> {
    return this.request<TikTokDeleteInstanceResult>(`${this.BASE}/instances/${instanceId}`, {
      method: 'DELETE',
    });
  }

  // ─────────────── OAuth ───────────────

  async startOAuth(instanceId: number): Promise<TikTokOAuthStartOut> {
    return this.request<TikTokOAuthStartOut>(
      `${this.BASE}/instances/${instanceId}/oauth/start`,
      { method: 'POST' },
    );
  }

  // ─────────────── Чаты (инбокс) ───────────────

  // Сортировка last_message_at DESC. scope read. (handoff: query-параметров нет.)
  async listChats(instanceId: number): Promise<TikTokChatOut[]> {
    return this.request<TikTokChatOut[]>(`${this.BASE}/instances/${instanceId}/chats`);
  }

  // ─────────────── Сообщения ───────────────

  // chatId — НАШ PK (TikTokChat.id, int). timestamp ASC (старые сверху). scope read.
  async listMessages(
    chatId: number,
    opts?: { limit?: number; offset?: number },
  ): Promise<TikTokMessageOut[]> {
    return this.request<TikTokMessageOut[]>(
      `${this.BASE}/chats/${chatId}/messages${buildQuery({
        limit: opts?.limit,
        offset: opts?.offset,
      })}`,
    );
  }

  // Текст 1..1000. Внутри 48ч-окна. scope send.
  async sendText(chatId: number, body: TikTokSendTextPayload): Promise<TikTokMessageOut> {
    return this.request<TikTokMessageOut>(`${this.BASE}/chats/${chatId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // ─────────────── private ───────────────

  private async request<T>(url: string, init?: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await authService.fetchWithAuth(url, init);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Нет соединения с сервером';
      throw new TikTokApiError(msg);
    }
    if (!res.ok) {
      throw new TikTokApiError(await extractDetail(res), res.status);
    }
    if (res.status === 204) {
      return undefined as unknown as T;
    }
    return (await res.json()) as T;
  }
}

const tiktokService = new TikTokService();
export default tiktokService;
